import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Card, handlePageClick, Layout, ModalV2, Pagination, Tabs } from '@utilities';

import VideoPlayer from './components/VideoPlayer';

import styles from './_index.module.scss';

const PAGE_SIZE = 10;

const Video = ({ data }) => {
  const [pagesByTab, setPagesByTab] = useState({});
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);

  const segments = data?.segments || [];
  const [selectedCompareBy, setSelectedCompareBy] = useState(segments[0]?.quotaGroupRef ?? null);

  const activeSegment =
    segments.find((segment) => segment.quotaGroupRef === selectedCompareBy) || segments[0] || null;
  const currentPage = pagesByTab[selectedCompareBy] ?? 1;

  const pagedResponses = useMemo(() => {
    const list = activeSegment?.responses ?? [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [activeSegment, currentPage]);

  const totalResponses = activeSegment?.responses?.length ?? 0;
  const pageCount = Math.ceil(totalResponses / PAGE_SIZE);

  useEffect(() => {
    if (!segments.length) {
      setSelectedCompareBy(null);
      return;
    }

    setSelectedCompareBy((prev) =>
      segments.some((segment) => segment.quotaGroupRef === prev) ? prev : segments[0]?.quotaGroupRef
    );
  }, [segments, selectedCompareBy]);

  useEffect(() => {
    if (!selectedCompareBy) return;
    setPagesByTab((prev) => ({
      ...prev,
      [selectedCompareBy]: 1,
    }));
  }, [selectedCompareBy]);

  const onPageChange = (event) => {
    const results = activeSegment?.responses ?? [];
    handlePageClick({
      event,
      items: results,
      itemsPerPage: PAGE_SIZE,
      setOffset: (offset) => {
        const nextPage = Math.floor(offset / PAGE_SIZE) + 1;
        setPagesByTab((prev) => ({
          ...prev,
          [selectedCompareBy]: nextPage,
        }));
      },
    });
  };

  const handleClosePlayer = () => {
    setIsPlayerOpen(false);
    setSelectedVideoIndex(null);
  };

  const handlePreviousVideo = () => {
    if (selectedVideoIndex > 0) {
      setSelectedVideoIndex(selectedVideoIndex - 1);
    }
  };

  const handleNextVideo = () => {
    if (selectedVideoIndex < allResponses.length - 1) {
      setSelectedVideoIndex(selectedVideoIndex + 1);
    }
  };

  const allResponses = activeSegment?.responses ?? [];
  const selectedVideo = selectedVideoIndex !== null ? allResponses[selectedVideoIndex] : null;
  const hasPrevious = selectedVideoIndex > 0;
  const hasNext = selectedVideoIndex !== null && selectedVideoIndex < allResponses.length - 1;

  return (
    <>
      <Tabs
        onChange={setSelectedCompareBy}
        options={data.segments.map(({ compareBy, quotaGroupRef }) => ({
          label: compareBy,
          value: quotaGroupRef,
        }))}
        value={selectedCompareBy}
      />
      {activeSegment && (
        <Layout.Flex.Column as="ul">
          {pagedResponses.map((response, pageIndex) => {
            const globalIndex = (currentPage - 1) * PAGE_SIZE + pageIndex;
            return (
              <li key={response.responseId}>
                <Card
                  className={styles['video-card-response-container']}
                  onClick={() => {
                    setSelectedVideoIndex(globalIndex);
                    setIsPlayerOpen(true);
                  }}
                >
                  <Layout.Flex.Row className={styles['video-card-content']}>
                    {response.thumbnailUrl && (
                      <img
                        alt="Video thumbnail"
                        className={styles['video-card-thumbnail']}
                        src={response.thumbnailUrl}
                      />
                    )}
                    <div className={styles['video-card-summary']}>
                      <p className={styles['video-card-summary-text']}>
                        {response.textTranscript} Will be replaced with AI generated summary.
                      </p>
                      <p className={styles['video-ai-summary-text']}>
                        Summary generated by Numerator AI
                      </p>
                    </div>
                  </Layout.Flex.Row>
                </Card>
              </li>
            );
          })}
          <Pagination onPageChange={onPageChange} pageCount={pageCount} show={pageCount > 1} />
        </Layout.Flex.Column>
      )}

      {selectedVideo && (
        <ModalV2 isActive={isPlayerOpen} onClose={handleClosePlayer} size="fullscreen">
          <VideoPlayer
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onNext={handleNextVideo}
            onPrevious={handlePreviousVideo}
            videoData={{
              json_transcript: selectedVideo.jsonTranscript,
              video_url: selectedVideo.videoUrl,
            }}
          />
        </ModalV2>
      )}
    </>
  );
};

Video.propTypes = {
  data: PropTypes.object.isRequired,
};

export default Video;
